import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from './Button';
import { IconPencil, IconX, IconSearch } from '@tabler/icons-react';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SEVERITY_OPTIONS = [
  { value: 'mild',             label: 'Mild' },
  { value: 'moderate',         label: 'Moderate' },
  { value: 'severe',           label: 'Severe' },
  { value: 'life_threatening', label: 'Life-threatening' },
];

const CATEGORY_OPTIONS = [
  { value: 'drug',          label: 'Drug' },
  { value: 'food',          label: 'Food' },
  { value: 'latex',         label: 'Latex' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'other',         label: 'Other' },
];

const CATEGORY_LABEL = CATEGORY_OPTIONS.reduce((a, o) => { a[o.value] = o.label; return a; }, {});
const SEVERITY_LABEL = SEVERITY_OPTIONS.reduce((a, o) => { a[o.value] = o.label; return a; }, {});

const SUBTYPE_LABEL = {
  allergy: 'Allergy',
  intolerance: 'Intolerance',
  unspecified: 'Unspecified',
};

// form.subtype is '' (none selected) in the UI; mapped to 'unspecified' on save.
const EMPTY_FORM = {
  allergy_type:        'drug',
  subtype:             '',     // '' | 'allergy' | 'intolerance'
  allergen:            '',
  catalog_id:          null,
  allergen_catalog_id: null,
  reaction:            '',
  severity:            '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatAuditDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// ---------------------------------------------------------------------------
// AllergyModal
// ---------------------------------------------------------------------------
export default function AllergyModal({ patient, resolveUser, onClose, onSaved }) {
  const [nkda, setNkda] = useState(patient.nkda);
  const [nka,  setNka]  = useState(patient.nka);
  const [allergyList, setAllergyList] = useState((patient.allergies || []).map(a => ({ ...a })));

  const [form, setForm]             = useState(EMPTY_FORM);
  const [editingId, setEditingId]   = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formError, setFormError]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  // Typeahead state
  const [results, setResults]       = useState({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
  const [searching, setSearching]   = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasPicked, setHasPicked]   = useState(false);   // a catalog pick is active
  const [highlight, setHighlight]   = useState(-1);      // index into the flat nav list; -1 = none
  const searchTimer = useRef(null);
  const resultsMenuRef = useRef(null);
  const allergenInputRef = useRef(null);

  const hasDrugAllergy = allergyList.some(a => a.allergy_type === 'drug');
  const hasAnyAllergy  = allergyList.length > 0;
  const isEditMode     = editingId !== null;

  const flagRows = [
    ...(nka  ? [{ key: 'nka',  label: 'NKA',  sub: 'No known allergies' }]      : []),
    ...(nkda ? [{ key: 'nkda', label: 'NKDA', sub: 'No known drug allergies' }] : []),
  ];

  // -------------------------------------------------------------------------
  // NKDA / NKA
  // -------------------------------------------------------------------------
  const handleNkda = async (checked) => {
    setGlobalError('');
    try {
      await api.patch(`/patients/${patient.id}/allergy-flags`, { nkda: checked, nka: false });
      setNkda(checked);
      if (checked) setNka(false);
      onSaved();
    } catch (e) {
      setGlobalError(e?.response?.data?.detail || 'Failed to update NKDA.');
    }
  };

  const handleNka = async (checked) => {
    setGlobalError('');
    try {
      await api.patch(`/patients/${patient.id}/allergy-flags`, { nkda: false, nka: checked });
      setNka(checked);
      if (checked) setNkda(false);
      onSaved();
    } catch (e) {
      setGlobalError(e?.response?.data?.detail || 'Failed to update NKA.');
    }
  };

  // -------------------------------------------------------------------------
  // Category change — resets allergen + pick; Latex auto-fills
  // -------------------------------------------------------------------------
  const selectCategory = (cat) => {
    setFormError('');
    setForm(f => ({
      ...f,
      allergy_type: cat,
      allergen: '',
      catalog_id: null,
      allergen_catalog_id: null,
    }));
    setHasPicked(false);
    setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
    setShowResults(false);
    if (cat === 'latex') {
      // Auto-resolve the single Latex catalog row.
      resolveLatex();
    } else {
      // Land focus in the allergen field for typing.
      setTimeout(() => { if (allergenInputRef.current) allergenInputRef.current.focus(); }, 50);
    }
  };

  const resolveLatex = async () => {
    try {
      const res = await api.get('/reference/allergens/search', { params: { q: 'latex' } });
      const row = (res.data.common_allergens || [])[0];
      if (row) {
        setForm(f => ({
          ...f,
          allergy_type: 'latex',
          allergen: row.label,
          allergen_catalog_id: row.allergen_catalog_id,
          catalog_id: null,
        }));
        setHasPicked(true);
      } else {
        // Fallback: just set the literal label.
        setForm(f => ({ ...f, allergy_type: 'latex', allergen: 'Latex' }));
      }
    } catch {
      setForm(f => ({ ...f, allergy_type: 'latex', allergen: 'Latex' }));
    }
  };

  // -------------------------------------------------------------------------
  // Typeahead search — scoped by category, with drug fallback
  // -------------------------------------------------------------------------
  const runSearch = useCallback(async (term, category) => {
    const q = term.trim();
    if (q.length < 2) {
      setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
      setShowResults(false);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/reference/allergens/search', { params: { q } });
      const data = res.data || {};
      const allCommon = data.common_allergens || [];
      const drugs = data.drugs || [];
      const classes = data.drug_classes || [];
      // RxNorm fallback names — the backend populates this ONLY when the
      // curated catalog had no drug match (Option B). Safe to pass wherever
      // drugs show; it's [] whenever the catalog matched.
      const rxnorm = data.rxnorm || [];

      if (category === 'drug') {
        setResults({ drugs, drug_classes: classes, common: [], rxnorm });
      } else {
        // Show only commons matching the selected category.
        const scopedCommon = allCommon.filter(c => c.category === category);
        if (scopedCommon.length > 0) {
          // A real non-drug match — no drug/RxNorm fallback.
          setResults({ drugs: [], drug_classes: [], common: scopedCommon, rxnorm: [] });
        } else {
          // Safety fallback: drugs + classes + RxNorm (picking switches to Drug).
          setResults({ drugs, drug_classes: classes, common: [], rxnorm });
        }
      }
      setShowResults(true);
    } catch {
      setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
      setShowResults(true);
    } finally {
      setSearching(false);
    }
  }, []);

  const onAllergenChange = (value) => {
    setFormError('');
    setHasPicked(false);
    setForm(f => ({ ...f, allergen: value, catalog_id: null, allergen_catalog_id: null }));
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (form.allergy_type === 'latex') return; // latex doesn't search
    if (value.trim().length < 2) {
      setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
      setShowResults(false);
      return;
    }
    const cat = form.allergy_type;
    searchTimer.current = setTimeout(() => runSearch(value, cat), 250);
  };

  // -------------------------------------------------------------------------
  // Selection handlers
  // -------------------------------------------------------------------------
  const pickDrug = (d) => {
    setForm(f => ({
      ...f,
      allergy_type: 'drug',          // a drug pick is always a drug allergy
      allergen: d.display_label,     // faithful label: brand if brand row, else generic
      catalog_id: d.catalog_id,      // always the ingredient anchor
      allergen_catalog_id: null,
    }));
    setHasPicked(true);
    setShowResults(false);
  };

  const pickClass = (c) => {
    setForm(f => ({
      ...f,
      allergy_type: 'drug',          // class handle is a drug-category allergy
      allergen: c.label,
      allergen_catalog_id: c.allergen_catalog_id,
      catalog_id: null,
    }));
    setHasPicked(true);
    setShowResults(false);
  };

  const pickCommon = (c) => {
    setForm(f => ({
      ...f,
      allergy_type: c.category || f.allergy_type,  // category follows the catalog row
      allergen: c.label,
      allergen_catalog_id: c.allergen_catalog_id,
      catalog_id: null,
    }));
    setHasPicked(true);
    setShowResults(false);
  };

  const pickRxNorm = (r) => {
    setForm(f => ({
      ...f,
      allergy_type: 'drug',          // it's a drug, just not in the catalog
      allergen: r.display_label,     // stored as free text (no catalog anchor)
      catalog_id: null,
      allergen_catalog_id: null,
    }));
    setHasPicked(true);
    setShowResults(false);
  };

  // -------------------------------------------------------------------------
  // Type toggle (no default; click again to deselect)
  // -------------------------------------------------------------------------
  const toggleSubtype = (val) => {
    setForm(f => ({ ...f, subtype: f.subtype === val ? '' : val }));
  };

  // -------------------------------------------------------------------------
  // Drawer open/close
  // -------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setConfirmDel(false);
    setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
    setShowResults(false);
    setHasPicked(false);
    setDrawerOpen(true);
    setTimeout(() => { if (allergenInputRef.current) allergenInputRef.current.focus(); }, 60);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      allergy_type:        a.allergy_type,
      subtype:             a.allergy_subtype === 'unspecified' ? '' : (a.allergy_subtype || ''),
      allergen:            a.allergen,
      catalog_id:          a.catalog_id ?? null,
      allergen_catalog_id: a.allergen_catalog_id ?? null,
      reaction:            a.reaction || '',
      severity:            a.severity || '',
    });
    setFormError('');
    setConfirmDel(false);
    setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
    setShowResults(false);
    setHasPicked(!!(a.catalog_id || a.allergen_catalog_id));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setConfirmDel(false);
    setResults({ drugs: [], drug_classes: [], common: [], rxnorm: [] });
    setShowResults(false);
    setHasPicked(false);
  };

  const validateForm = () => {
    if (!form.allergen.trim()) {
      setFormError('Allergen is required.');
      return false;
    }
    return true;
  };

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    setFormError('');
    setGlobalError('');

    const payload = {
      allergy_type:        form.allergy_type,
      allergy_subtype:     form.subtype || 'unspecified',
      allergen:            form.allergen.trim(),
      catalog_id:          form.catalog_id,
      allergen_catalog_id: form.allergen_catalog_id,
      reaction:            form.reaction.trim() || null,
      severity:            form.severity || null,
    };

    try {
      if (isEditMode) {
        const res = await api.patch(`/patients/${patient.id}/allergies/${editingId}`, payload);
        setAllergyList(prev => prev.map(a => a.id === editingId ? res.data : a));
        if (payload.allergy_type === 'drug') { setNkda(false); setNka(false); }
      } else {
        const res = await api.post(`/patients/${patient.id}/allergies`, payload);
        setAllergyList(prev => [...prev, res.data]);
        if (payload.allergy_type === 'drug') { setNkda(false); setNka(false); }
        else { setNka(false); }
      }
      onSaved();
      closeDrawer();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to save allergy.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    setSaving(true);
    setGlobalError('');
    try {
      await api.delete(`/patients/${patient.id}/allergies/${editingId}`);
      setAllergyList(prev => prev.filter(a => a.id !== editingId));
      onSaved();
      closeDrawer();
    } catch {
      setGlobalError('Failed to delete allergy.');
    } finally {
      setSaving(false);
    }
  };

  const editingAllergy = isEditMode ? allergyList.find(a => a.id === editingId) : null;

  const hasResults = results.drugs.length > 0 || results.drug_classes.length > 0 || results.common.length > 0 || results.rxnorm.length > 0;
  const noMatch = showResults && !hasResults && !searching && form.allergen.trim().length >= 2 && !hasPicked;

  // Flatten the three divided sections into one ordered nav list so arrow keys
  // traverse them continuously. Order matches render order: common -> drugs ->
  // classes (the same order the menu lays them out). Each entry remembers its
  // kind so Enter dispatches to the correct pick handler.
  const navList = [
    ...results.common.map(item => ({ kind: 'common', item })),
    ...results.drugs.map(item => ({ kind: 'drug', item })),
    ...results.drug_classes.map(item => ({ kind: 'class', item })),
    ...results.rxnorm.map(item => ({ kind: 'rxnorm', item })),
  ];

  const pickByKind = (entry) => {
    if (!entry) return;
    if (entry.kind === 'common') pickCommon(entry.item);
    else if (entry.kind === 'drug') pickDrug(entry.item);
    else if (entry.kind === 'class') pickClass(entry.item);
    else if (entry.kind === 'rxnorm') pickRxNorm(entry.item);
  };

  // Keep highlight in range as the result set changes; reset to none on new search.
  useEffect(() => {
    setHighlight(-1);
  }, [form.allergen, form.allergy_type]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    if (!showResults || highlight < 0 || !resultsMenuRef.current) return;
    const row = resultsMenuRef.current.querySelector(`[data-nav="${highlight}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [highlight, showResults]);

  const onAllergenKeyDown = (e) => {
    if (form.allergy_type === 'latex') return;  // latex doesn't search
    if (e.key === 'ArrowDown') {
      if (!showResults && hasResults) { setShowResults(true); return; }
      if (navList.length === 0) return;
      e.preventDefault();
      setHighlight(h => Math.min(navList.length - 1, h < 0 ? 0 : h + 1));
    } else if (e.key === 'ArrowUp') {
      if (navList.length === 0) return;
      e.preventDefault();
      setHighlight(h => Math.max(0, h < 0 ? 0 : h - 1));
    } else if (e.key === 'Enter') {
      if (showResults && highlight >= 0 && navList[highlight]) {
        e.preventDefault();
        pickByKind(navList[highlight]);
      }
      // else: nothing highlighted -> let Enter bubble (free-text save path)
    } else if (e.key === 'Escape') {
      if (showResults) { e.preventDefault(); setShowResults(false); setHighlight(-1); }
    }
  };

  // -------------------------------------------------------------------------
  // Drawer render
  // -------------------------------------------------------------------------
  const renderDrawer = () => (
    <div style={{
      width: drawerOpen ? '380px' : '0px',
      opacity: drawerOpen ? 1 : 0,
      overflow: 'hidden',
      transition: 'width 0.25s ease, opacity 0.2s ease',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTop: '3px solid var(--color-accent-navy)',
      borderRight: drawerOpen ? '1px solid var(--color-border)' : 'none',
      backgroundColor: 'var(--color-bg-subtle)',
    }}>
      <div style={styles.drawerHeader}>
        <span style={styles.drawerTitle}>{isEditMode ? 'Edit Allergy' : 'Add Allergy'}</span>
        <button style={styles.drawerCloseBtn} onClick={closeDrawer} aria-label="Close drawer">
          <IconX size={16} stroke={2} />
        </button>
      </div>

      <div style={styles.drawerBody}>
        {/* 1. Category */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Category</label>
          <div style={styles.segmented}>
            {CATEGORY_OPTIONS.map(o => (
              <Button
                key={o.value}
                variant={form.allergy_type === o.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => selectCategory(o.value)}
                style={{ flex: '1 0 calc(33.333% - 4px)', minWidth: 0 }}
              >
                {o.label}
              </Button>
            ))}
          </div>
        </div>

        {/* 2. Allergen (typeahead) */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Allergen <span style={styles.required}>*</span></label>
          <div style={{ position: 'relative' }}>
            <div className="nephros-input-wrap" style={styles.searchWrap}>
              <IconSearch size={15} stroke={1.5} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <input
                ref={allergenInputRef}
                style={styles.searchInput}
                value={form.allergen}
                onChange={e => onAllergenChange(e.target.value)}
                onFocus={() => { if (hasResults) setShowResults(true); }}
                onKeyDown={onAllergenKeyDown}
                role="combobox"
                aria-expanded={showResults}
                placeholder={form.allergy_type === 'latex' ? 'Latex' : 'Type to search...'}
                autoComplete="off"
              />
              {searching && <span style={styles.searchingText}>...</span>}
            </div>

            {showResults && (hasResults || noMatch) && (
              <div ref={resultsMenuRef} style={styles.resultsMenu}>
                {results.common.length > 0 && (
                  <>
                    <div style={styles.resultSection}>{CATEGORY_LABEL[form.allergy_type]} allergens</div>
                    {results.common.map((c, i) => {
                      const navIdx = i;
                      return (
                      <div key={`cm-${c.allergen_catalog_id}`} data-nav={navIdx}
                        style={{ ...styles.resultRow, ...(highlight === navIdx ? styles.resultRowHi : {}) }}
                        onMouseEnter={() => setHighlight(navIdx)}
                        onMouseDown={() => pickCommon(c)}>
                        {c.label}
                      </div>
                      );
                    })}
                  </>
                )}
                {results.drugs.length > 0 && (
                  <>
                    <div style={styles.resultSection}>
                      {form.allergy_type === 'drug' ? 'Drugs' : 'Drug matches (switches category to Drug)'}
                    </div>
                    {results.drugs.map((d, i) => {
                      const navIdx = results.common.length + i;
                      return (
                      <div key={`dr-${d.is_brand ? 'b' : 'i'}-${d.catalog_id}-${i}`} data-nav={navIdx}
                        style={{ ...styles.resultRow, ...(highlight === navIdx ? styles.resultRowHi : {}) }}
                        onMouseEnter={() => setHighlight(navIdx)}
                        onMouseDown={() => pickDrug(d)}>
                        <span style={styles.resultPrimary}>{d.display_label}</span>
                        {d.is_brand && d.ingredient_name && (
                          <span style={styles.resultSub}>
                            {d.ingredient_name}
                            {d.sibling_brands && d.sibling_brands.length > 0
                              ? ` \u00b7 also ${d.sibling_brands.join(', ')}`
                              : ''}
                          </span>
                        )}
                        {!d.is_brand && d.sibling_brands && d.sibling_brands.length > 0 && (
                          <span style={styles.resultSub}>{d.sibling_brands.join(', ')}</span>
                        )}
                      </div>
                      );
                    })}
                  </>
                )}
                {results.drug_classes.length > 0 && (
                  <>
                    <div style={styles.resultSection}>Drug classes</div>
                    {results.drug_classes.map((c, i) => {
                      const navIdx = results.common.length + results.drugs.length + i;
                      return (
                      <div key={`cl-${c.allergen_catalog_id}`} data-nav={navIdx}
                        style={{ ...styles.resultRow, ...(highlight === navIdx ? styles.resultRowHi : {}) }}
                        onMouseEnter={() => setHighlight(navIdx)}
                        onMouseDown={() => pickClass(c)}>
                        {c.label}
                      </div>
                      );
                    })}
                  </>
                )}
                {results.rxnorm.length > 0 && (
                  <>
                    <div style={styles.resultSection}>RxNorm — not in catalog, saved as typed</div>
                    {results.rxnorm.map((r, i) => {
                      const navIdx = results.common.length + results.drugs.length + results.drug_classes.length + i;
                      return (
                      <div key={`rx-${i}`} data-nav={navIdx}
                        style={{ ...styles.resultRow, ...(highlight === navIdx ? styles.resultRowHi : {}) }}
                        onMouseEnter={() => setHighlight(navIdx)}
                        onMouseDown={() => pickRxNorm(r)}>
                        {r.display_label}
                      </div>
                      );
                    })}
                  </>
                )}
                {noMatch && (
                  <div style={styles.noMatchHint}>
                    "{form.allergen.trim()}" will be added as typed.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 3. Type (allergy / intolerance, no default) */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Type</label>
          <div style={styles.toggleRow}>
            {['allergy', 'intolerance'].map(val => (
              <Button
                key={val}
                variant={form.subtype === val ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => toggleSubtype(val)}
                style={{ flex: 1 }}
              >
                {SUBTYPE_LABEL[val]}
              </Button>
            ))}
          </div>
          {!form.subtype && (
            <span style={styles.subtypeHint}>Optional — left blank, recorded as unspecified.</span>
          )}
        </div>

        {/* 4. Reaction */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Reaction</label>
          <input
            style={styles.input}
            value={form.reaction}
            onChange={e => setForm(f => ({ ...f, reaction: e.target.value }))}
            placeholder="e.g. Hives, anaphylaxis"
          />
        </div>

        {/* 5. Severity */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>Severity</label>
          <select
            style={styles.input}
            value={form.severity}
            onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
          >
            <option value="">— Select —</option>
            {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {formError && <div style={styles.formError}>{formError}</div>}

        {isEditMode && editingAllergy && (
          <div style={styles.auditLine}>
            {editingAllergy.modified_by
              ? `Modified by ${resolveUser(editingAllergy.modified_by)} on ${formatAuditDate(editingAllergy.updated_at)}`
              : `Added by ${resolveUser(editingAllergy.created_by)} on ${formatAuditDate(editingAllergy.created_at)}`}
          </div>
        )}
      </div>

      <div style={styles.drawerFooter}>
        {confirmDel ? (
          <div style={styles.confirmBox}>
            <div style={styles.confirmText}>This allergy will be permanently deleted.</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Confirm delete'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              {isEditMode && (
                <Button variant="danger" size="sm" onClick={() => setConfirmDel(true)} disabled={saving}>
                  Delete
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" onClick={closeDrawer} disabled={saving}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Table render
  // -------------------------------------------------------------------------
  const renderTable = () => {
    const hasContent = flagRows.length > 0 || allergyList.length > 0;
    if (!hasContent) {
      return <div style={styles.emptyState}>No allergy information documented.</div>;
    }
    const compact = drawerOpen;
    return (
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, textAlign: 'left' }}>Allergen</th>
              <th style={{ ...styles.th, width: '110px', textAlign: 'left' }}>Category</th>
              {!compact && <th style={{ ...styles.th, width: '110px', textAlign: 'left' }}>Type</th>}
              {!compact && <th style={{ ...styles.th, textAlign: 'left' }}>Reaction</th>}
              {!compact && <th style={{ ...styles.th, width: '115px', textAlign: 'left' }}>Severity</th>}
              <th style={{ ...styles.th, width: '36px' }} />
            </tr>
          </thead>
          <tbody>
            {flagRows.map(flag => (
              <tr key={flag.key} style={styles.flagTr}>
                <td style={{ ...styles.td, textAlign: 'left' }} colSpan={compact ? 2 : 5}>
                  <span style={styles.flagLabel}>{flag.label}</span>
                  <span style={styles.flagSub}> — {flag.sub}</span>
                </td>
                <td style={styles.tdPencil} />
              </tr>
            ))}
            {allergyList.map(a => {
              const isHovered = hoveredRow === a.id;
              const isEditing = editingId === a.id;
              return (
                <tr
                  key={a.id}
                  style={{
                    ...styles.tr,
                    backgroundColor: isEditing || isHovered ? 'var(--color-accent-wash)' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredRow(a.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => isEditing ? closeDrawer() : openEdit(a)}
                >
                  <td style={{ ...styles.td, textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <span style={styles.allergenName}>{a.allergen}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'left', color: 'var(--color-text-muted)' }}>
                    {CATEGORY_LABEL[a.allergy_type] || a.allergy_type}
                  </td>
                  {!compact && (
                    <td style={{ ...styles.td, textAlign: 'left', color: 'var(--color-text-muted)' }}>
                      {a.allergy_subtype && a.allergy_subtype !== 'unspecified'
                        ? SUBTYPE_LABEL[a.allergy_subtype] : '—'}
                    </td>
                  )}
                  {!compact && (
                    <td style={{ ...styles.td, textAlign: 'left', color: 'var(--color-text-muted)' }}
                        title={a.reaction || ''}>
                      {a.reaction || '—'}
                    </td>
                  )}
                  {!compact && (
                    <td style={{ ...styles.td, textAlign: 'left', color: 'var(--color-text-muted)' }}>
                      {a.severity ? (SEVERITY_LABEL[a.severity] || a.severity) : '—'}
                    </td>
                  )}
                  <td style={styles.tdPencil}>
                    {isHovered && <IconPencil size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)' }} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Allergies</h2>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            <IconX size={18} stroke={2} />
          </button>
        </div>

        {globalError && <div style={styles.errorBanner}>{globalError}</div>}

        <div style={styles.topBar}>
          <div style={styles.flagControls}>
            <label style={{ ...styles.flagCheck, opacity: hasDrugAllergy ? 0.4 : 1, cursor: hasDrugAllergy ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={nkda} disabled={hasDrugAllergy || saving}
                onChange={e => handleNkda(e.target.checked)} />
              <span style={styles.flagCheckText}>NKDA</span>
            </label>
            <label style={{ ...styles.flagCheck, opacity: hasAnyAllergy ? 0.4 : 1, cursor: hasAnyAllergy ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={nka} disabled={hasAnyAllergy || saving}
                onChange={e => handleNka(e.target.checked)} />
              <span style={styles.flagCheckText}>NKA</span>
            </label>
          </div>
          {!drawerOpen && (
            <Button variant="primary" size="sm" onClick={openAdd}>+ Add Allergen</Button>
          )}
        </div>

        <div style={styles.contentArea}>
          {renderDrawer()}
          <div style={{ ...styles.tablePane, paddingLeft: drawerOpen ? '16px' : '0' }}>{renderTable()}</div>
        </div>

        <div style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', width: '860px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 },
  modalTitle: { fontSize: '17px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex' },
  errorBanner: { margin: '12px 24px 0', padding: '10px 14px', backgroundColor: 'var(--color-severity-high-bg)', border: '1px solid var(--color-severity-high)', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--color-severity-high)', fontFamily: 'var(--font-family-base)', flexShrink: 0 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px 0 24px', flexShrink: 0 },
  flagControls: { display: 'flex', gap: '16px', alignItems: 'center' },
  flagCheck: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', userSelect: 'none' },
  flagCheckText: { fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  contentArea: { display: 'flex', flex: 1, overflow: 'hidden', padding: '12px 24px 20px 24px', gap: '0', minHeight: '340px', maxHeight: '600px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', padding: '12px 24px', borderTop: '1px solid var(--color-border)', flexShrink: 0 },

  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)', flexShrink: 0 },
  drawerTitle: { fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  drawerCloseBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', padding: '2px', borderRadius: '4px' },
  drawerBody: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  drawerFooter: { padding: '12px 16px', borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)', flexShrink: 0 },

  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  fieldLabel: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  required: { color: 'var(--color-severity-high)' },
  input: { width: '100%', padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },

  // Segmented category control
  segmented: { display: 'flex', flexWrap: 'wrap', gap: '4px' },

  // Search field
  searchWrap: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '13px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', backgroundColor: 'transparent' },
  searchingText: { fontSize: '12px', color: 'var(--color-text-muted)', flexShrink: 0 },
  resultsMenu: { position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 30, backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)', maxHeight: '220px', overflowY: 'auto' },
  resultSection: { fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 10px 4px', fontFamily: 'var(--font-family-base)' },
  resultRow: { padding: '7px 10px', fontSize: '13px', cursor: 'pointer', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)', display: 'flex', flexDirection: 'column', gap: '1px' },
  resultRowHi: { backgroundColor: 'var(--color-accent-tint)' },
  resultPrimary: { fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  resultSub: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  noMatchHint: { padding: '10px', fontSize: '12px', color: 'var(--color-text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)' },

  // Type toggle
  toggleRow: { display: 'flex', gap: '8px' },
  subtypeHint: { fontSize: '11px', color: 'var(--color-text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)' },

  formError: { fontSize: 'var(--font-size-xs)', color: 'var(--color-severity-high)', fontFamily: 'var(--font-family-base)' },
  auditLine: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', fontStyle: 'italic', paddingTop: '8px', borderTop: '1px solid var(--color-border)', marginTop: '4px' },
  confirmBox: { padding: '12px', borderRadius: '4px', border: '1px solid var(--color-severity-high)', backgroundColor: 'var(--color-severity-high-bg)' },
  confirmText: { fontSize: '13px', color: 'var(--color-severity-high)', fontWeight: 600, marginBottom: '10px', fontFamily: 'var(--font-family-base)' },

  tablePane: { flex: 1, overflowY: 'auto' },
  tableWrapper: { border: '1px solid var(--color-border)', borderTop: '3px solid var(--color-accent-navy)', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 'var(--font-size-table)', fontFamily: 'var(--font-family-base)' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap', backgroundColor: 'var(--color-subNav)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderBottom: '1px solid var(--color-border)', transition: 'background-color 0.15s ease' },
  flagTr: { borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-subtle)' },
  td: { padding: '10px 12px', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' },
  tdPencil: { padding: '10px 8px', width: '36px', textAlign: 'center', verticalAlign: 'middle' },
  flagLabel: { fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  flagSub: { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-base)' },
  allergenName: { fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  emptyState: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', fontStyle: 'italic', padding: '48px 24px', textAlign: 'center' },
};